import { useMemo, useState } from 'react'
import { Badge, Button, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import { CONNECTED_DEVICES, CGM_DATA } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

type ConnectedDevice = (typeof CONNECTED_DEVICES)[number]

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint

const MAX_GLUCOSE = Math.max(...CGM_DATA.map((d) => d.glucose))
const TARGET_GLUCOSE = 100
const avgGlucose = Math.round(CGM_DATA.reduce((sum, d) => sum + d.glucose, 0) / CGM_DATA.length)
const inRangeCount = CGM_DATA.filter((d) => d.glucose >= 70 && d.glucose <= 140).length
const timeInRange = Math.round((inRangeCount / CGM_DATA.length) * 100)
const glucoseValues = CGM_DATA.map((d) => d.glucose)
const glucoseMean = avgGlucose
const glucoseVariability = Math.round(
  Math.sqrt(glucoseValues.reduce((sum, v) => sum + (v - glucoseMean) ** 2, 0) / glucoseValues.length)
)

const CGM_STATS = [
  { label: 'Avg Glucose', value: `${avgGlucose}`, unit: 'mg/dL' },
  { label: 'Time in Range (70–140)', value: `${timeInRange}`, unit: '%' },
  { label: 'Glucose Variability (SD)', value: `${glucoseVariability}`, unit: 'mg/dL' },
]

function cloneDevices(): ConnectedDevice[] {
  return CONNECTED_DEVICES.map((device) => ({ ...device }))
}

export default function WearablesTab() {
  const [devices, setDevices] = useState<ConnectedDevice[]>(() => cloneDevices())
  const [syncingDeviceId, setSyncingDeviceId] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  const connectedCount = devices.filter((device) => device.connected).length
  const totalDevices = devices.length

  const stats = useMemo(
    () => [
      { label: 'Total Devices', value: totalDevices },
      { label: 'Connected', value: connectedCount },
      { label: 'Data Points Synced Today', value: '2,847' },
    ],
    [connectedCount, totalDevices]
  )

  async function updateDeviceConnection(deviceId: string, nextConnected: boolean) {
    if (syncingDeviceId) {
      return
    }

    const target = devices.find((device) => device.id === deviceId)
    if (!target) {
      setStatusMessage('[DEVICE_NOT_FOUND] Unable to update unknown device.')
      return
    }

    setStatusMessage(null)
    setSyncingDeviceId(deviceId)

    await new Promise((resolve) => {
      window.setTimeout(resolve, 320)
    })

    setDevices((current) =>
      current.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              connected: nextConnected,
              lastSync: nextConnected ? 'Just now' : 'Not connected',
            }
          : device
      )
    )

    setSyncingDeviceId(null)
    setStatusMessage(
      nextConnected
        ? `${target.name} connected and synced.`
        : `${target.name} disconnected.`
    )
  }

  function handleConnectDevice() {
    const nextDisconnected = devices.find((device) => !device.connected)
    if (!nextDisconnected) {
      setStatusMessage('All listed devices are already connected.')
      return
    }

    void updateDeviceConnection(nextDisconnected.id, true)
  }

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="wearables-header-shell">
        <div className="wearables-header-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Wearables command center</Subheading>
              <Badge variant="zinc">
                {connectedCount}/{totalDevices} connected
              </Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button type="button" onClick={handleConnectDevice} disabled={Boolean(syncingDeviceId)}>
                Connect next device
              </Button>
              {statusMessage && (
                <p className={`text-sm/6 ${TEXT_SECONDARY}`} aria-live="polite">
                  {statusMessage}
                </p>
              )}
            </div>
          </div>

          <div className="wearables-summary-grid">
            {stats.map((stat, index) => (
              <GradientSphereMetricCard
                key={stat.label}
                label={stat.label}
                value={stat.value}
                sphereColor={sphereColorAt(index)}
              />
            ))}
          </div>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Connected devices</Subheading>
          <Badge variant="zinc">{connectedCount} active</Badge>
        </div>

        <div className="wearables-device-list mt-4">
          {devices.map((device) => {
            const syncing = syncingDeviceId === device.id

            return (
              <SurfaceRow key={device.id} className="wearables-device-row">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm/6 font-bold ${TEXT_PRIMARY}`}>{device.name}</span>
                    <Badge variant="zinc">
                      {device.connected ? 'Connected' : 'Disconnected'}
                    </Badge>
                  </div>

                  <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs/5 ${TEXT_SUBTLE}`}>
                    <span>
                      {device.type} &middot; {device.brand}
                    </span>
                    {device.battery !== null && <span>Battery {device.battery}%</span>}
                    <span>Last sync: {device.lastSync}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {device.metrics.map((metric) => (
                      <Badge key={metric} variant="zinc">
                        {metric}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="shrink-0">
                  <Button
                    variant={device.connected ? 'secondary' : 'default'}
                    className=""
                    disabled={Boolean(syncingDeviceId)}
                    onClick={() => void updateDeviceConnection(device.id, !device.connected)}
                  >
                    {syncing
                      ? 'Syncing...'
                      : device.connected
                        ? 'Disconnect'
                        : 'Connect'}
                  </Button>
                </div>
              </SurfaceRow>
            )
          })}
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Continuous glucose signal</Subheading>
          <Badge variant="zinc">Target {TARGET_GLUCOSE} mg/dL</Badge>
        </div>

        <div className="wearables-cgm-layout mt-5">
          <div className="relative flex h-56 items-end gap-2">
            <div
              className="pointer-events-none absolute left-0 right-0 border-t-2 border-dashed dash-viz-target-line"
              style={{ bottom: `${(TARGET_GLUCOSE / MAX_GLUCOSE) * 100}%` }}
            />

            {CGM_DATA.map((point) => {
              const heightPct = Math.max(8, (point.glucose / MAX_GLUCOSE) * 100)
              const isAboveTarget = point.glucose > TARGET_GLUCOSE
              return (
                <div key={point.time} className="flex flex-1 flex-col items-center gap-1.5">
                  <span className={`text-[11px] font-medium ${TEXT_SUBTLE}`}>{point.glucose}</span>
                  <div
                    className={`w-full rounded-t-lg ${
                      isAboveTarget
                        ? 'dash-viz-gradient-strong'
                        : 'bg-linear-to-t from-accent to-accent-soft'
                    }`}
                    style={{ height: `${heightPct}%` }}
                  />
                  <span className={`text-[10px] leading-tight ${TEXT_FAINT}`}>
                    {point.time.replace(':00 ', ' ').replace(':00', '')}
                  </span>
                </div>
              )
            })}
          </div>

          <div className="wearables-cgm-stats">
            {CGM_STATS.map((stat, index) => (
              <GradientSphereMetricCard
                key={stat.label}
                label={stat.label}
                sphereColor={sphereColorAt(index)}
                value={
                  <>
                    {stat.value}
                    <span className="gradient-sphere-metric-card__unit">{stat.unit}</span>
                  </>
                }
              />
            ))}
          </div>
        </div>
      </SurfaceCard>
    </div>
  )
}
